import React, { createContext, useContext, useState, useEffect } from 'react';
import { clientApi } from '../services/api';

const NotificationContext = createContext();

export const useNotifications = () => useContext(NotificationContext);

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [todayFollowUps, setTodayFollowUps] = useState([]);
  const [unseenClients, setUnseenClients] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load notifications on mount
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        setLoading(true);
        const clients = await clientApi.getAll();
        
        // Get today's date (without time)
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        
        // Find clients with follow-ups today
        const followUpsToday = clients.filter(client => {
          if (!client.nextFollowUp) return false;
          
          const followUpDate = new Date(client.nextFollowUp);
          followUpDate.setHours(0, 0, 0, 0);
          
          return followUpDate.getTime() === today.getTime();
        });
        
        setTodayFollowUps(followUpsToday);
        setUnseenClients([]); // No longer tracking unseen clients
        
        // Only include follow-up notifications
        const allNotifications = [
          ...followUpsToday.map(client => ({
            id: `followup-${client.id}`,
            type: 'followup',
            client,
            message: `Follow-up scheduled today for ${client.firstName} ${client.lastName || ''}`,
          })),
          // Initial contact notifications removed as requested
        ];
        
        setNotifications(allNotifications);
        setLoading(false);
      } catch (error) {
        console.error('Error loading notifications:', error);
        setLoading(false);
      }
    };
    
    fetchNotifications();
    
    // Refresh notifications every 5 minutes
    const interval = setInterval(() => {
      fetchNotifications();
    }, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, []);
  
  // Mark notification as read
  const markAsRead = (notificationId) => {
    setNotifications(prev => 
      prev.filter(notification => notification.id !== notificationId)
    );
  };
  
  // Mark all notifications as read
  const markAllAsRead = () => {
    setNotifications([]);
  };
  
  const value = {
    notifications,
    todayFollowUps,
    unseenClients,
    loading,
    markAsRead,
    markAllAsRead,
    totalCount: notifications.length,
  };
  
  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
